/**
 * Lookup de atribuição Meta por lead — orquestra Supabase (lead, tracking, conversa).
 */
import { supabase } from '../../../lib/supabase';
import { sanitizeCtwaClid, type MetaAttribution } from './metaAttributionService';

export interface AttributionResult {
    hasAttribution: boolean;
    attribution?: MetaAttribution | null;
    conversationId?: string;
    contactId?: string;
    phone?: string;
    firstName?: string;
    lastName?: string;
    email?: string;
    origin?: string | null;
    firstTouchSource?: string | null;
    firstTouchAt?: string | null;
    wonAt?: string | null;
    error?: string;
}

const TRACKING_FIELDS =
    'first_touch_ctwa_clid, first_touch_at, first_touch_source, first_touch_campaign_name, first_touch_adset_name, first_touch_ad_name, first_touch_campaign_id, first_touch_adset_id, first_touch_ad_id';

const CONVERSATION_FIELDS = 'id, meta_attribution';

function pickString(value: unknown): string | null {
    return typeof value === 'string' && value.trim() ? value.trim() : null;
}

function mapTrackingRow(row: Record<string, unknown>) {
    return {
        ctwaClid: sanitizeCtwaClid(row.first_touch_ctwa_clid),
        firstTouchAt: (row.first_touch_at as string | null) ?? null,
        firstTouchSource: (row.first_touch_source as string | null) ?? null,
        campaignName: pickString(row.first_touch_campaign_name),
        adsetName: pickString(row.first_touch_adset_name),
        adName: pickString(row.first_touch_ad_name),
        campaignId: pickString(row.first_touch_campaign_id),
        adsetId: pickString(row.first_touch_adset_id),
        adId: pickString(row.first_touch_ad_id),
    };
}

async function findConversationWithAttribution(
    organizationId: string,
    contactId: string | undefined,
    cleanPhone: string,
    trackingCtwaClid: string | null,
) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const strategies: Array<(q: any) => any> = [];
    if (contactId) {
        // Instagram Direct: remote_jid = ig:{igsid}, sem telefone
        strategies.push((q) =>
            q.eq('contact_id', contactId).eq('platform', 'instagram').not('meta_attribution', 'is', null),
        );
        strategies.push((q) => q.eq('contact_id', contactId).not('meta_attribution', 'is', null));
    }
    if (cleanPhone) {
        strategies.push(
            (q) => q.eq('contact_phone', cleanPhone).not('meta_attribution', 'is', null),
            (q) => q.ilike('remote_jid', `${cleanPhone}%`).not('meta_attribution', 'is', null),
        );
    }
    if (trackingCtwaClid && contactId) {
        strategies.push((q) => q.eq('contact_id', contactId));
    }

    for (const apply of strategies) {
        const { data, error } = await apply(
            supabase.from('chat_conversations').select(CONVERSATION_FIELDS).eq('organization_id', organizationId)
        )
            .order('created_at', { ascending: false })
            .limit(1)
            .maybeSingle();
        if (error) return { conversation: null, error };
        if (data) return { conversation: data, error: null };
    }

    return { conversation: null, error: null };
}

/**
 * Busca dados de atribuição Meta associados a um Lead.
 * Navega: Lead -> Contact -> Phone -> Conversation -> Attribution
 */
export async function getAttributionByLeadId(
    leadId: string,
    organizationId: string
): Promise<AttributionResult> {
    try {
        if (!organizationId) {
            return { hasAttribution: false, error: 'organization_id obrigatório' };
        }

        const { data: lead, error: leadError } = await supabase
            .from('opportunities')
            .select(`
                id,
                organization_id,
                contact_id,
                origin,
                won_at,
                contact:contacts (
                    name,
                    phone,
                    whatsapp,
                    email
                )
            `)
            .eq('id', leadId)
            .eq('organization_id', organizationId)
            .maybeSingle();

        if (leadError) {
            console.warn('[MetaAttribution] Erro ao buscar lead:', leadId, leadError.message);
            return { hasAttribution: false, error: 'Erro ao buscar lead' };
        }

        if (!lead) {
            return { hasAttribution: false, error: 'Lead não encontrado' };
        }

        const contactRaw = lead.contact as { name?: string; phone?: string; whatsapp?: string; email?: string } | { name?: string; phone?: string; whatsapp?: string; email?: string }[] | null;
        const contact = Array.isArray(contactRaw) ? contactRaw[0] : contactRaw;
        const phone = contact?.whatsapp || contact?.phone;

        const nameParts = contact?.name?.trim().split(/\s+/);
        const firstName = nameParts?.[0];
        const lastName = nameParts && nameParts.length > 1 ? nameParts.slice(1).join(' ') : undefined;
        const cleanPhone = phone ? phone.replace(/\D/g, '') : '';
        const dealOrigin = (lead.origin as string | null) ?? null;
        const dealWonAt = (lead.won_at as string | null) ?? null;
        const contactId = (lead.contact_id as string | null) ?? undefined;

        let tracking = mapTrackingRow({});
        if (contactId) {
            const { data: trackingRow } = await supabase
                .from('lead_tracking')
                .select(TRACKING_FIELDS)
                .eq('organization_id', organizationId)
                .eq('contact_id', contactId)
                .maybeSingle();
            if (trackingRow) tracking = mapTrackingRow(trackingRow);
        }

        const { conversation, error: convError } = await findConversationWithAttribution(
            organizationId,
            contactId,
            cleanPhone,
            tracking.ctwaClid,
        );

        const rawAttr = (conversation?.meta_attribution ?? null) as MetaAttribution | null;
        const hasIgSid = Boolean(rawAttr?.ig_sid);

        // WhatsApp/CTWA precisa de telefone; Instagram Direct usa ig_sid
        if (!cleanPhone && !hasIgSid) {
            return {
                hasAttribution: false,
                contactId,
                firstName,
                lastName,
                email: contact?.email,
                origin: dealOrigin,
                firstTouchSource: tracking.firstTouchSource,
                firstTouchAt: tracking.firstTouchAt,
                wonAt: dealWonAt,
                conversationId: conversation?.id,
                attribution: rawAttr || undefined,
                error: 'Lead sem telefone e sem conversa Instagram atribuída',
            };
        }

        const contactContext = {
            contactId,
            phone: cleanPhone || undefined,
            firstName,
            lastName,
            email: contact?.email,
            origin: dealOrigin,
            firstTouchSource: tracking.firstTouchSource,
            firstTouchAt: tracking.firstTouchAt,
            wonAt: dealWonAt,
        };

        if (convError || !conversation || (!conversation.meta_attribution && !tracking.ctwaClid)) {
            console.warn('[MetaAttribution] Sem conversa atribuída — contato disponível para offline:', {
                phone: cleanPhone || null,
                contactId,
                hasConversation: !!conversation,
            });

            return {
                hasAttribution: false,
                ...contactContext,
                conversationId: conversation?.id,
                attribution: conversation?.meta_attribution
                    ? (conversation.meta_attribution as MetaAttribution)
                    : undefined,
                error: 'Sem atribuição CTWA/IG',
            };
        }

        const raw = (conversation.meta_attribution ?? {}) as MetaAttribution;
        const validCtwa = sanitizeCtwaClid(raw.ctwa_clid) ?? tracking.ctwaClid;
        const attribution: MetaAttribution = {
            ...raw,
            ctwa_clid: validCtwa,
            captured_at: raw.captured_at || undefined,
            campaign_id: raw.campaign_id || tracking.campaignId,
            campaign_name: raw.campaign_name || tracking.campaignName,
            adset_id: raw.adset_id || tracking.adsetId,
            adset_name: raw.adset_name || tracking.adsetName,
            ad_id: raw.ad_id || tracking.adId,
            ad_name: raw.ad_name || tracking.adName,
        };

        const needsPersist = validCtwa && (
            !raw.ctwa_clid ||
            !conversation.meta_attribution ||
            (!raw.campaign_name && tracking.campaignName) ||
            (!raw.adset_name && tracking.adsetName) ||
            (!raw.ad_name && tracking.adName)
        );

        if (needsPersist) {
            await supabase
                .from('chat_conversations')
                .update({ meta_attribution: attribution })
                .eq('id', conversation.id)
                .eq('organization_id', organizationId);
        }

        if (!attribution.ctwa_clid && !attribution.dataset_id && !attribution.ig_sid) {
            return {
                hasAttribution: false,
                attribution,
                conversationId: conversation.id,
                ...contactContext,
                error: 'Atribuição sem clid e sem dataset',
            };
        }

        return {
            hasAttribution: true,
            attribution,
            conversationId: conversation.id,
            ...contactContext,
        };
    } catch (error: unknown) {
        const message = error instanceof Error ? error.message : 'Erro inesperado';
        return { hasAttribution: false, error: message };
    }
}

export type MetaIntegrationRow = { config?: Record<string, unknown> } | null;

export async function fetchConnectedIntegration(
    organizationId: string,
    type: 'instagram_dm' | 'meta_ads'
): Promise<MetaIntegrationRow> {
    const { data } = await supabase
        .from('integrations')
        .select('config')
        .eq('organization_id', organizationId)
        .eq('type', type)
        .eq('status', 'connected')
        .maybeSingle();
    return data;
}

export function resolveMetaDatasetId(attribution: MetaAttribution, integration: MetaIntegrationRow): string | null {
    const config = integration?.config;
    return (
        attribution.dataset_id ||
        attribution.pixel_id ||
        (config?.dataset_id as string | undefined) ||
        (config?.pixel_id as string | undefined) ||
        null
    );
}

export async function patchConversationMetaAttribution(
    conversationId: string,
    organizationId: string,
    oldAttribution: MetaAttribution,
    value: number,
    patch: Partial<MetaAttribution>
) {
    try {
        await supabase
            .from('chat_conversations')
            .update({ meta_attribution: { ...oldAttribution, ...patch, conversion_value: value } })
            .eq('id', conversationId)
            .eq('organization_id', organizationId);
    } catch (e) {
        console.warn('[MetaEvents] Falha ao atualizar meta_attribution na conversa:', e);
    }
}

export async function markAttributedConversion(
    conversationId: string,
    organizationId: string,
    attribution: MetaAttribution,
    value: number
) {
    await patchConversationMetaAttribution(conversationId, organizationId, attribution, value, {
        conversion_sent: true,
        conversion_attributed: true,
        conversion_sent_at: new Date().toISOString(),
    });
}

export async function markOfflineConversion(
    conversationId: string,
    organizationId: string,
    attribution: MetaAttribution,
    value: number
) {
    await patchConversationMetaAttribution(conversationId, organizationId, attribution, value, {
        conversion_offline_sent: true,
        conversion_offline_sent_at: new Date().toISOString(),
    });
}
